import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'hooks/useTranslation';
import { Loaders } from 'shared';

import { SuccessfulActionModal } from 'features/dashboard/components/SuccessfulActionModal';
import { FailedActionModal } from 'features/dashboard/components/FailedActionModal';
import Modal from 'features/app/components/Modal';
import { TopUpBalanceForm } from 'features/finance/components/TopUpBalanceForm';
import { getFormattedAmountWithCurrency } from 'features/finance/utils/get-formatted-amount-with-currency';
import { ReactComponent as SuccessfulTopUpIcon } from 'assets/icons/successful-top-up-icon.svg';

import { useStyles } from './styles';
import { useHistory, useLocation } from 'react-router-dom';
import { getQueryParamFromSearch } from 'utils';
import { useStripe } from '@stripe/react-stripe-js';

interface TopUpBalanceModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: () => Promise<unknown>;
}

export type TopUpStatus = {
  isModalShown: boolean;
  type: 'success' | 'fail' | 'processing' | '';
  message?: string;
  amount?: number;
  currency?: string;
};

export const initialTopUpStatusState: TopUpStatus = {
  isModalShown: false,
  type: '',
  message: '',
  amount: 0,
  currency: '',
};

export const TopUpBalanceModal: React.FC<TopUpBalanceModalProps> = ({ onClose, onSubmit: onModalSubmit, open }) => {
  const classes = useStyles();
  const { t } = useTranslation('finance');
  const { search } = useLocation();
  const history = useHistory();
  const stripe = useStripe();
  const paymentIntentClientSecret = useMemo(
    () => getQueryParamFromSearch(search, 'payment_intent_client_secret'),
    [search]
  );

  const [topUpStatus, setTopUpStatus] = useState<TopUpStatus>(initialTopUpStatusState);

  const handleClose = async () => {
    onClose();
  };

  const onProcessing = () => {
    setTopUpStatus({
      isModalShown: false,
      type: 'processing',
    });
  };

  const onModalClose = () => {
    setTopUpStatus(initialTopUpStatusState);
    history.replace({
      search: '',
    });
  };

  const onTopUpSuccess = useCallback(
    (amount: number, currency: string) => {
      onClose();
      setTopUpStatus({
        isModalShown: true,
        type: 'success',
        amount: amount,
        currency: currency,
      });
    },
    [onClose]
  );

  const onTopUpFailed = useCallback(
    (message?: string) => {
      onClose();
      setTopUpStatus({
        isModalShown: true,
        message: message || '',
        type: 'fail',
      });
    },
    [onClose]
  );

  useEffect(() => {
    if (!stripe || !paymentIntentClientSecret || topUpStatus.isModalShown) return;
    (async () => {
      const response = await stripe.retrievePaymentIntent(paymentIntentClientSecret);
      const intent = response.paymentIntent;
      if (!intent) return;
      const errorMsg = intent.last_payment_error?.message;
      if (errorMsg) onTopUpFailed(errorMsg);
      else onTopUpSuccess(Number(localStorage.getItem('latestTopUpAmount') || 0), intent.currency.toUpperCase());
    })();
  }, [paymentIntentClientSecret, stripe, onTopUpSuccess, onTopUpFailed, topUpStatus.isModalShown]);

  const ResponseModal = () => {
    return topUpStatus.type === 'success' ? (
      <SuccessfulActionModal
        title={`${getFormattedAmountWithCurrency(topUpStatus.amount as number, topUpStatus.currency as string)} \n ${t(
          'topUpSuccessful'
        )}`}
        icon={SuccessfulTopUpIcon}
        onClose={onModalClose}
        dialogContentClass={classes.dialogContent}
        open={true}
      />
    ) : (
      <FailedActionModal
        error={topUpStatus.message}
        open={true}
        onClose={onModalClose}
        dialogContentClass={classes.dialogContent}
      />
    );
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={t('topUpBalance')}
        rootClass={classes.topUpBalanceModal}
        paperClass={classes.topUpBalanceModalPaper}
        dialogContentClass={classes.topUpBalanceModalContent}
      >
        {topUpStatus.type === 'processing' ? (
          <Loaders.Loader />
        ) : (
          <TopUpBalanceForm
            onSubmit={onModalSubmit}
            onProcessing={onProcessing}
            onTopUpSuccess={onTopUpSuccess}
            onTopUpFailed={onTopUpFailed}
          />
        )}
      </Modal>

      {topUpStatus.isModalShown ? <ResponseModal /> : null}
    </>
  );
};
